/**
 * @module router/navigationGuard
 *
 * Global navigation guard that runs before every route change.
 * Enforces authentication and authorization rules:
 *
 * 1. Routes with meta.requiresAuth redirect to login if user is not logged in
 * 2. Routes with meta.requiresAdmin redirect non-admin users to last visited page
 * 3. Already logged-in users are redirected away from the login page to last visited page
 * 4. All other navigation is allowed through
 */

import { useLogin } from "@/composables/useLogin";

// Get auth state (these are module-scoped singletons from useLogin)
const { loggedIn, isAdmin } = useLogin();

/**
 * Returns the last visited page name from localStorage,
 * falling back to "list-podcasts" if none is stored.
 */
function getLastPage() {
  return localStorage.getItem("fyt-last-page") || "list-podcasts";
}

/**
 * Global before-each navigation guard.
 * Checks route metadata and user authentication state to determine
 * whether to allow, redirect, or block the navigation.
 *
 * @param {import('vue-router').RouteLocationNormalized} to - Target route
 * @param {import('vue-router').RouteLocationNormalized} from - Current route
 * @param {Function} next - Callback to resolve the navigation
 */
export const navigationGuard = (to, from, next) => {
  if (to.meta.requiresAuth && !loggedIn.value) {
    // Route requires authentication but user is not logged in -- redirect to login
    next({ name: "login" });
  } else if (to.meta.requiresAdmin && !isAdmin.value) {
    // Route requires admin role but user is not an admin -- redirect to last visited page
    next({ name: getLastPage() });
  } else if (to.name === "login" && loggedIn.value) {
    // Logged-in users can't go (back) to the login page -- send them home.
    // Use the stable home route rather than getLastPage() (which can be stale
    // or, if it ever resolved to login, loop).
    next({ name: "list-podcasts" });
  } else {
    // All checks passed -- allow navigation
    next();
  }
};
